import React, { useState, useEffect, useRef } from "react";
import { useMessaging } from "../../context/MessagingContext";
import { useUserRole } from "../../context/UserRoleContext";
import MessageBubble from "./MessageBubble";
import ConfirmModal from "../common/ConfirmModal";

const GroupAvatarIcon = () => (
  <svg
    className="group-avatar-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="6.6" r="2.5" fill="currentColor" />
    <path
      d="M8.6 13.3c.8-1.3 2-2 3.4-2s2.6.7 3.4 2c.6.9.9 1.9 1 3v.8H7.6v-.8c.1-1.1.4-2.1 1-3z"
      fill="currentColor"
    />
    <circle cx="6.4" cy="9.6" r="2.2" fill="currentColor" />
    <path
      d="M2.4 16.5c.1-.9.4-1.7.9-2.4.7-1.1 1.7-1.7 3.1-1.7.8 0 1.5.2 2.1.6-.5.5-.9 1.1-1.3 1.7-.3.5-.5 1.1-.7 1.8H2.4z"
      fill="currentColor"
    />
    <circle cx="17.6" cy="9.6" r="2.2" fill="currentColor" />
    <path
      d="M21.6 16.5c-.1-.9-.4-1.7-.9-2.4-.7-1.1-1.7-1.7-3.1-1.7-.8 0-1.5.2-2.1.6.5.5.9 1.1 1.3 1.7.3.5.5 1.1.7 1.8h4.1z"
      fill="currentColor"
    />
  </svg>
);

const ChatWindow = ({
  conversation,
  onArchive,
  showBackButton = false,
  onBackToInbox,
}) => {
  const {
    messages,
    loadingMessages,
    sendMessage,
    joinConversation,
    leaveConversation,
    fetchMessages,
    startTyping,
    stopTyping,
    typingUsers,
    markMessagesAsRead,
    archiveConversation,
    renameGroupConversation,
  } = useMessaging();
  const { userData } = useUserRole();

  const [messageText, setMessageText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const conversationId = conversation?.conversation_id;
  const conversationMessages = messages[conversationId] || [];
  const conversationTypingUsers = typingUsers[conversationId] || [];
  const isLoadingMessages = loadingMessages[conversationId] === true;
  const isGroupConversation = conversation?.type === "group";

  useEffect(() => {
    if (conversation) {
      setEditedTitle(conversation.title || "");
      setIsEditingTitle(false);
    }
  }, [conversation]);

  // Join conversation when selected
  useEffect(() => {
    if (conversationId) {
      joinConversation(conversationId);

      return () => {
        leaveConversation();
      };
    }
  }, [conversationId, joinConversation, leaveConversation]);

  // Mark messages as read when conversation is opened
  useEffect(() => {
    if (conversationId && markMessagesAsRead) {
      // Mark all messages in conversation as read
      markMessagesAsRead(conversationId);
    }
  }, [conversationId, markMessagesAsRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      startTyping(conversationId);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      stopTyping(conversationId);
    }, 1000);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (messageText.trim() && conversationId) {
      sendMessage(conversationId, messageText);
      setMessageText("");

      // Stop typing indicator
      if (isTyping) {
        setIsTyping(false);
        stopTyping(conversationId);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!conversationId) return;

    setIsArchiving(true);
    try {
      await archiveConversation(conversationId, true);
      if (onArchive) {
        onArchive();
      }
      setShowArchiveConfirm(false);
    } catch (error) {
      console.error("Error archiving conversation:", error);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleArchiveCancel = () => {
    setShowArchiveConfirm(false);
  };

  const handleStartEditTitle = () => {
    setEditedTitle(conversation?.title || "");
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setEditedTitle(conversation?.title || "");
    setIsEditingTitle(false);
  };

  const handleSaveEditTitle = async () => {
    if (!conversationId || !isGroupConversation) return;

    setIsSavingTitle(true);
    try {
      await renameGroupConversation(conversationId, editedTitle.trim());
      setIsEditingTitle(false);
    } catch (error) {
      console.error("Error saving conversation title:", error);
    } finally {
      setIsSavingTitle(false);
    }
  };

  if (!conversation) {
    return (
      <div className="chat-window-empty">
        <div className="empty-state">
          <span className="empty-icon">💬</span>
          <h2>Select a conversation</h2>
          <p>Choose a conversation from the list to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      {/* Chat Header */}
      <div className="chat-header">
        {showBackButton && (
          <button
            type="button"
            className="chat-back-btn"
            onClick={onBackToInbox}
            aria-label="Back to conversations"
          >
            ← Back
          </button>
        )}
        <div className="chat-header-info">
          <div className="chat-avatar">
            {isGroupConversation ? (
              <GroupAvatarIcon />
            ) : (
              conversation.display_name?.charAt(0).toUpperCase() || "?"
            )}
          </div>
          <div>
            {isGroupConversation && isEditingTitle ? (
              <div className="chat-name-edit-row">
                <input
                  type="text"
                  className="chat-name-input"
                  value={editedTitle}
                  maxLength={255}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  placeholder="Group Chat"
                  disabled={isSavingTitle}
                />
                <button
                  type="button"
                  className="chat-title-action-btn"
                  onClick={handleSaveEditTitle}
                  disabled={isSavingTitle}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="chat-title-action-btn secondary"
                  onClick={handleCancelEditTitle}
                  disabled={isSavingTitle}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="chat-name-row">
                <h3 className="chat-name">{conversation.display_name}</h3>
                {isGroupConversation && (
                  <button
                    type="button"
                    className="chat-title-edit-btn"
                    onClick={handleStartEditTitle}
                    aria-label="Edit group name"
                    title="Edit group name"
                  >
                    ✏️
                  </button>
                )}
              </div>
            )}
            {conversation.type === "group" && conversation.participants?.length > 0 && (
              <div className="chat-group-members">
                {(conversation.participants || [])
                  .map((p) => p.name)
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
            {conversationTypingUsers.length > 0 && (
              <div className="typing-indicator-text">typing...</div>
            )}
          </div>
        </div>

        <div className="chat-header-actions">
          <button
            type="button"
            className="chat-archive-btn"
            onClick={handleArchiveClick}
            aria-label="Archive conversation"
            title="Archive conversation"
          >
            🗑️ <span>Archive</span>
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="chat-messages" ref={messagesContainerRef}>
        {isLoadingMessages ? (
          <div className="chat-messages-loading">
            <div className="spinner"></div>
            <p>Loading messages...</p>
          </div>
        ) : conversationMessages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {conversationMessages.map((message, index) => {
              const isOwnMessage = message.sender_id === userData?.user_id;
              const prevMessage = conversationMessages[index - 1];
              const showSender =
                !prevMessage || prevMessage.sender_id !== message.sender_id;

              return (
                <MessageBubble
                  key={message.message_id}
                  message={message}
                  isOwnMessage={isOwnMessage}
                  showSender={showSender}
                  conversationType={conversation?.type}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <form className="chat-input-container" onSubmit={handleSendMessage}>
        <textarea
          className="chat-input"
          value={messageText}
          onChange={(e) => {
            setMessageText(e.target.value);
            handleTyping();
          }}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          rows="1"
        />
        <button
          type="submit"
          className="chat-send-btn"
          disabled={!messageText.trim()}
        >
          <span className="send-icon">➤</span>
        </button>
      </form>

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        open={showArchiveConfirm}
        title="Archive Conversation"
        message="Are you sure you want to archive this conversation? This will remove it from your messages."
        confirmLabel="Archive"
        cancelLabel="Cancel"
        isConfirming={isArchiving}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />
    </div>
  );
};

export default ChatWindow;
